/**
 * Integrytas Group - Base App (MOBILE)
 * Page: ISR.TARGETS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/IsrTargetsPage.tsx. A DISTINCT
 * presentation (KPI tiles + dense card list + facet chips + bottom-sheet detail
 * with an inline edit sheet) over the SAME data/logic layer — never the desktop
 * grid re-hosted (§16):
 *   - reads via apiDb over isr_target / isr_state / isr_run_log, plus
 *     une_workspace + upp_definition to resolve the workspace and IMPORT-definition
 *     FKs to their names (§17.B4 — no raw id ever reaches render);
 *   - every mutation (activate/deactivate and the field edits) routes through the
 *     Common Routine via routedUpdate — §2 single write path, never a direct
 *     apiDb insert/update/delete;
 *   - run_status / protocol / auth / schedule are CHECK-enums, so their displayed
 *     labels resolve through i18n via labelled constants, never the stored literal
 *     (§17.B11). Y/N renders as an m-pill status pill (§17.B10).
 *   - "Run Now" triggers the sanctioned ISR dispatch RPC (isr_dispatch_one) — an
 *     async operation trigger, not a business-data table write, and the only path
 *     the platform exposes for on-demand polling (mirrors the desktop page).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, DownloadCloud, Play, SquarePen } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill, type MobileLeadTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import type { AuthSession } from '../../types/auth';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ISR.TARGETS';

// ---------------------------------------------------------------------------
// Data shapes (mirroring the columns the desktop page selects).
// ---------------------------------------------------------------------------

interface TargetRow {
  isr_target_id: number;
  internal_code: string;
  name: string;
  une_workspace_id: number | null;
  protocol: string;
  endpoint_url: string;
  auth_mechanism: string;
  secret_ref: string | null;
  upp_definition_id: number | null;
  schedule_kind: string;
  cron_expr: string | null;
  interval_seconds: number;
  timeout_ms: number;
  is_active: 'Y' | 'N';
}

interface StateRow {
  isr_target_id: number;
  last_successful_sync_at: string | null;
  high_water_mark: string | null;
  run_status: string;
  consecutive_failures: number;
  last_error: string | null;
  next_run_at: string | null;
}

interface RunRow {
  isr_run_log_id: number;
  status: string;
  rows_fetched: number;
  rows_ingested: number;
  message: string | null;
  created_at: string;
}

interface WsLite { une_workspace_id: number; name: string }
interface DefLite { upp_definition_id: number; internal_code: string; name: string }

// ---------------------------------------------------------------------------
// CHECK-enum → localized label + pill/lead tone (§17.B11). The stored literal is
// never rendered; the label resolves through i18n with a neutral English default.
// ---------------------------------------------------------------------------

type PillTone = 'm-p-ok' | 'm-p-info' | 'm-p-wait' | 'm-p-no' | '';

interface RunStatusMeta { labelKey: string; label: string; pill: PillTone; lead: MobileLeadTone }

const RUN_STATUS_META: Record<string, RunStatusMeta> = {
  IDLE:    { labelKey: 'mobile.isr.status.IDLE',    label: 'Idle',    pill: '',         lead: 'slate' },
  RUNNING: { labelKey: 'mobile.isr.status.RUNNING', label: 'Running', pill: 'm-p-info', lead: 'blue'  },
  OK:      { labelKey: 'mobile.isr.status.OK',      label: 'OK',      pill: 'm-p-ok',   lead: 'green' },
  FAILED:  { labelKey: 'mobile.isr.status.FAILED',  label: 'Failed',  pill: 'm-p-no',   lead: 'red'   },
  ERROR:   { labelKey: 'mobile.isr.status.ERROR',   label: 'Error',   pill: 'm-p-no',   lead: 'red'   },
};

interface EnumOption { value: string; labelKey: string; label: string }

const PROTOCOL_OPTIONS: EnumOption[] = [
  { value: 'HTTP', labelKey: 'mobile.isr.protocol.HTTP', label: 'HTTP' },
  { value: 'SFTP', labelKey: 'mobile.isr.protocol.SFTP', label: 'SFTP' },
];

const AUTH_OPTIONS: EnumOption[] = [
  { value: 'NONE',    labelKey: 'mobile.isr.auth.NONE',    label: 'None' },
  { value: 'BEARER',  labelKey: 'mobile.isr.auth.BEARER',  label: 'Bearer token' },
  { value: 'BASIC',   labelKey: 'mobile.isr.auth.BASIC',   label: 'Basic' },
  { value: 'API_KEY', labelKey: 'mobile.isr.auth.API_KEY', label: 'API key' },
  { value: 'SSH_KEY', labelKey: 'mobile.isr.auth.SSH_KEY', label: 'SSH key' },
];

const SCHEDULE_OPTIONS: EnumOption[] = [
  { value: 'INTERVAL', labelKey: 'mobile.isr.schedule.INTERVAL', label: 'Interval' },
  { value: 'CRON',     labelKey: 'mobile.isr.schedule.CRON',     label: 'Cron' },
];

type TFn = ReturnType<typeof useTranslation>['t'];

function enumLabel(t: TFn, options: EnumOption[], value: string): string {
  const opt = options.find((o) => o.value === value);
  return opt ? t(opt.labelKey, opt.label) : value;
}

// Run-status facet buckets for the chip row.
type RunFacet = 'all' | 'ok' | 'running' | 'failed' | 'idle';

// Active-status facet buckets for the second (independent) chip row.
type ActiveFacet = 'all' | 'active' | 'inactive';

function facetBucket(runStatus: string): Exclude<RunFacet, 'all'> {
  switch (runStatus) {
    case 'OK': return 'ok';
    case 'RUNNING': return 'running';
    case 'FAILED':
    case 'ERROR': return 'failed';
    default: return 'idle';
  }
}

// ---------------------------------------------------------------------------
// Main screen.
// ---------------------------------------------------------------------------

export function MobileIsrTargetsPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const fmtDateTime = useFormatDateTime();
  const [rows, setRows] = useState<TargetRow[]>([]);
  const [states, setStates] = useState<Record<number, StateRow>>({});
  const [ws, setWs] = useState<WsLite[]>([]);
  const [defs, setDefs] = useState<DefLite[]>([]);
  const [filter, setFilter] = useState('');
  const [runFacet, setRunFacet] = useState<RunFacet>('all');
  const [activeFacet, setActiveFacet] = useState<ActiveFacet>('active');
  const [selected, setSelected] = useState<TargetRow | null>(null);
  const [editing, setEditing] = useState<TargetRow | null>(null);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [tRes, sRes, wRes, dRes] = await Promise.all([
        apiDb.from('isr_target').select('isr_target_id, internal_code, name, une_workspace_id, protocol, endpoint_url, auth_mechanism, secret_ref, upp_definition_id, schedule_kind, cron_expr, interval_seconds, timeout_ms, is_active').order('internal_code'),
        apiDb.from('isr_state').select('isr_target_id, last_successful_sync_at, high_water_mark, run_status, consecutive_failures, last_error, next_run_at'),
        apiDb.from('une_workspace').select('une_workspace_id, name').eq('is_active', 'Y').order('name'),
        apiDb.from('upp_definition').select('upp_definition_id, internal_code, name').eq('direction', 'IMPORT').eq('is_active', 'Y').order('internal_code'),
      ]);
      if (tRes.error) throw tRes.error;
      setRows((tRes.data ?? []) as TargetRow[]);
      const sm: Record<number, StateRow> = {};
      for (const r of (sRes.data ?? []) as StateRow[]) sm[r.isr_target_id] = r;
      setStates(sm);
      setWs((wRes.data ?? []) as WsLite[]);
      setDefs((dRes.data ?? []) as DefLite[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Load once on mount. Workspace/definition labels are not locale-derived here
  // (neither entity carries a _tl), so no active-locale dependency is needed.
  useEffect(() => { void reload(); }, []);

  // Load the recent run log for whichever target is currently open in the sheet.
  useEffect(() => {
    const id = (editing ?? selected)?.isr_target_id;
    if (id == null) { setRuns([]); return; }
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb
        .from('isr_run_log')
        .select('isr_run_log_id, status, rows_fetched, rows_ingested, message, created_at')
        .eq('isr_target_id', id)
        .order('created_at', { ascending: false })
        .limit(10);
      if (!cancelled) setRuns((data ?? []) as RunRow[]);
    })();
    return () => { cancelled = true; };
  }, [selected, editing]);

  const wsName = (id: number | null): string => {
    if (id == null) return t('common.none', 'None');
    return ws.find((w) => w.une_workspace_id === id)?.name ?? t('common.none', 'None');
  };
  const defLabel = (id: number | null): string => {
    if (id == null) return t('mobile.isr.no_definition', 'No import definition');
    const d = defs.find((x) => x.upp_definition_id === id);
    return d ? (d.name || d.internal_code) : t('mobile.isr.no_definition', 'No import definition');
  };

  const statusOf = (r: TargetRow): string => states[r.isr_target_id]?.run_status ?? 'IDLE';
  const metaOf = (r: TargetRow): RunStatusMeta => RUN_STATUS_META[statusOf(r)] ?? RUN_STATUS_META.IDLE;
  const scheduleText = (r: TargetRow): string =>
    r.schedule_kind === 'CRON'
      ? (r.cron_expr || t('mobile.isr.no_cron', 'No cron set'))
      : t('mobile.isr.every_seconds', { count: r.interval_seconds, defaultValue: `every ${r.interval_seconds}s` });

  const kpis = useMemo(() => {
    let active = 0;
    let running = 0;
    let failing = 0;
    for (const r of rows) {
      if (r.is_active === 'Y') active += 1;
      const st = states[r.isr_target_id];
      if (st?.run_status === 'RUNNING') running += 1;
      if ((st?.consecutive_failures ?? 0) > 0 || st?.run_status === 'FAILED' || st?.run_status === 'ERROR') failing += 1;
    }
    return { total: rows.length, active, running, failing };
  }, [rows, states]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (runFacet !== 'all' && facetBucket(statusOf(r)) !== runFacet) return false;
      if (activeFacet === 'active' && r.is_active !== 'Y') return false;
      if (activeFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        r.protocol.toLowerCase().includes(q) ||
        r.endpoint_url.toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, runFacet, activeFacet, states]);

  const facets: { key: RunFacet; label: string }[] = [
    { key: 'all',     label: t('mobile.isr.facet.all', 'All') },
    { key: 'ok',      label: t('mobile.isr.status.OK', 'OK') },
    { key: 'running', label: t('mobile.isr.status.RUNNING', 'Running') },
    { key: 'failed',  label: t('mobile.isr.status.FAILED', 'Failed') },
    { key: 'idle',    label: t('mobile.isr.status.IDLE', 'Idle') },
  ];

  const activeFacets: { key: ActiveFacet; label: string }[] = [
    { key: 'all',      label: t('mobile.isr.facet.all', 'All') },
    { key: 'active',   label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const runStatusPill = (code: string) => {
    const meta = RUN_STATUS_META[code] ?? RUN_STATUS_META.IDLE;
    return <span className={'m-pill ' + meta.pill}>{t(meta.labelKey, meta.label)}</span>;
  };

  const toggleActive = async (r: TargetRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'isr_target', 'isr_target_id', r.isr_target_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  const runNow = async (r: TargetRow) => {
    setActionBusy(true);
    setError(null);
    setNotice(null);
    try {
      // Sanctioned ISR dispatch trigger (async worker) — not a table write.
      const { error: e } = await apiDb.rpc('isr_dispatch_one', { p_target_id: r.isr_target_id });
      if (e) throw e;
      setNotice(t('mobile.isr.dispatched', 'Poll dispatched — the worker runs asynchronously; the run log refreshes shortly.'));
      setTimeout(() => {
        void (async () => {
          const { data } = await apiDb
            .from('isr_run_log')
            .select('isr_run_log_id, status, rows_fetched, rows_ingested, message, created_at')
            .eq('isr_target_id', r.isr_target_id)
            .order('created_at', { ascending: false })
            .limit(10);
          setRuns((data ?? []) as RunRow[]);
        })();
      }, 1500);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  const closeSheet = () => { if (!actionBusy) { setSelected(null); setNotice(null); } };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.isr.title', 'Sync Targets')}
        description={t('mobile.isr.description', 'Inbound sync feeds, their schedule and last run status.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.total, label: t('mobile.isr.kpi.total', 'Targets') },
          { value: kpis.active, label: t('mobile.isr.kpi.active', 'Active') },
          { value: kpis.running, label: t('mobile.isr.kpi.running', 'Running') },
          { value: kpis.failing, label: t('mobile.isr.kpi.failing', 'Failing') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.isr.search', 'Search targets')}
          aria-label={t('mobile.isr.search', 'Search targets')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => runFacet === v}
        onSelect={(v) => setRunFacet(v as RunFacet)}
      />

      <MobileFilterChips
        chips={activeFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => activeFacet === v}
        onSelect={(v) => setActiveFacet(v as ActiveFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.isr.none', 'No targets match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => {
            const meta = metaOf(r);
            const st = states[r.isr_target_id];
            const sub = `${enumLabel(t, PROTOCOL_OPTIONS, r.protocol)} · ${scheduleText(r)}`
              + (st && st.consecutive_failures > 0 ? ` · ${t('mobile.isr.fail_count', { count: st.consecutive_failures, defaultValue: `${st.consecutive_failures} fails` })}` : '');
            return (
              <MobileCardRow
                key={r.isr_target_id}
                lead={<DownloadCloud />}
                leadTone={meta.lead}
                title={r.name || r.internal_code}
                subtitle={sub}
                trailing={
                  <>
                    {runStatusPill(statusOf(r))}
                    <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                      {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                    </MobileStatusPill>
                  </>
                }
                chevron
                onClick={() => setSelected(r)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={closeSheet} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.isr.detail', 'Target detail')}>
        <div className="m-grabber" />
        {selected && (() => {
          const st = states[selected.isr_target_id];
          return (
            <>
              <div className="m-sheet-id">
                <span className="m-av"><DownloadCloud size={20} /></span>
                <span>
                  <div className="m-nm">{selected.name || selected.internal_code}</div>
                  <div className="m-em">{selected.internal_code}</div>
                </span>
              </div>

              <MobileContentCard>
                <MobileDetailRow label={t('mobile.isr.f.status', 'Status')} value={runStatusPill(statusOf(selected))} />
                <MobileDetailRow
                  label={t('mobile.isr.f.active', 'Active')}
                  value={
                    <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                      {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                    </MobileStatusPill>
                  }
                />
                <MobileDetailRow label={t('mobile.isr.f.workspace', 'Workspace')} value={wsName(selected.une_workspace_id)} />
                <MobileDetailRow label={t('mobile.isr.f.protocol', 'Protocol')} value={enumLabel(t, PROTOCOL_OPTIONS, selected.protocol)} />
                <MobileDetailRow label={t('mobile.isr.f.endpoint', 'Endpoint')} value={selected.endpoint_url || '—'} />
                <MobileDetailRow label={t('mobile.isr.f.auth', 'Auth')} value={enumLabel(t, AUTH_OPTIONS, selected.auth_mechanism)} />
                <MobileDetailRow label={t('mobile.isr.f.definition', 'Import definition')} value={defLabel(selected.upp_definition_id)} />
                <MobileDetailRow label={t('mobile.isr.f.schedule', 'Schedule')} value={`${enumLabel(t, SCHEDULE_OPTIONS, selected.schedule_kind)} · ${scheduleText(selected)}`} />
                <MobileDetailRow label={t('mobile.isr.f.last_sync', 'Last sync')} value={st?.last_successful_sync_at ? fmtDateTime(st.last_successful_sync_at) : '—'} />
                <MobileDetailRow label={t('mobile.isr.f.next_run', 'Next run')} value={st?.next_run_at ? fmtDateTime(st.next_run_at) : '—'} />
                <MobileDetailRow
                  label={t('mobile.isr.f.failures', 'Consecutive failures')}
                  value={
                    <span className={'m-pill ' + ((st?.consecutive_failures ?? 0) > 0 ? 'm-p-no' : '')}>
                      {(st?.consecutive_failures ?? 0).toLocaleString()}
                    </span>
                  }
                />
                {st?.last_error && (
                  <MobileDetailRow
                    label={t('mobile.isr.f.last_error', 'Last error')}
                    value={<span style={{ color: 'var(--color-danger)' }}>{st.last_error}</span>}
                  />
                )}
              </MobileContentCard>

              <div className="m-sect"><h2>{t('mobile.isr.recent_runs', 'Recent runs')}</h2></div>
              <MobileContentCard>
                {runs.length === 0 ? (
                  <div className="m-empty">{t('mobile.isr.no_runs', 'No runs yet')}</div>
                ) : (
                  runs.map((run) => (
                    <MobileDetailRow
                      key={run.isr_run_log_id}
                      label={fmtDateTime(run.created_at)}
                      value={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {runStatusPill(run.status)}
                          <span className="m-formrow-hint">
                            {t('mobile.isr.run_counts', { fetched: run.rows_fetched, ingested: run.rows_ingested, defaultValue: `${run.rows_fetched} fetched / ${run.rows_ingested} ingested` })}
                          </span>
                        </span>
                      }
                    />
                  ))
                )}
              </MobileContentCard>

              {notice && (
                <MobileContentCard><div className="m-empty">{notice}</div></MobileContentCard>
              )}

              <div className="m-actionbar">
                {canChange && (<button
                  type="button"
                  className="m-btn m-btn-primary"
                  disabled={actionBusy}
                  onClick={() => { setEditing(selected); setSelected(null); }}
                >
                  <SquarePen size={16} />
                  {t('mobile.isr.edit', 'Edit')}
                </button>)}
                <button
                  type="button"
                  className="m-btn m-btn-ghost"
                  disabled={actionBusy}
                  onClick={() => void runNow(selected)}
                >
                  <Play size={16} />
                  {t('mobile.isr.run_now', 'Run now')}
                </button>
                {canChange && (<button
                  type="button"
                  className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                  disabled={actionBusy}
                  onClick={() => void toggleActive(selected)}
                >
                  <Power size={16} />
                  {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
                </button>)}
                <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={closeSheet} aria-label={t('mobile.close', 'Close')}>
                  <X size={16} />
                </button>
              </div>
            </>
          );
        })()}
      </div>

      <IsrTargetEditSheet
        session={session}
        target={editing}
        ws={ws}
        defs={defs}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inline edit sheet — the touch-first form. Saves the common scalar/config fields
// through the Common Routine (routedUpdate) — §2 single write path. Role-style
// heavy config (the raw Config JSON) stays on desktop; the mobile form edits the
// day-to-day fields.
// ---------------------------------------------------------------------------

interface EditSheetProps {
  session: AuthSession;
  target: TargetRow | null;
  ws: WsLite[];
  defs: DefLite[];
  onClose: () => void;
  onSaved: () => void;
}

interface EditForm {
  name: string;
  wsId: number | null;
  protocol: string;
  endpoint: string;
  auth: string;
  secretRef: string;
  defId: number | null;
  schedKind: string;
  cron: string;
  interval: number;
  timeout: number;
  active: boolean;
}

function formFromTarget(target: TargetRow): EditForm {
  return {
    name: target.name ?? '',
    wsId: target.une_workspace_id,
    protocol: target.protocol,
    endpoint: target.endpoint_url ?? '',
    auth: target.auth_mechanism,
    secretRef: target.secret_ref ?? '',
    defId: target.upp_definition_id,
    schedKind: target.schedule_kind,
    cron: target.cron_expr ?? '',
    interval: target.interval_seconds,
    timeout: target.timeout_ms,
    active: target.is_active === 'Y',
  };
}

function IsrTargetEditSheet({ session, target, ws, defs, onClose, onSaved }: EditSheetProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<EditForm>(() => ({
    name: '', wsId: null, protocol: 'HTTP', endpoint: '', auth: 'NONE', secretRef: '',
    defId: null, schedKind: 'INTERVAL', cron: '', interval: 300, timeout: 30000, active: false,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different target is opened.
  useEffect(() => {
    if (!target) return;
    setForm(formFromTarget(target));
    setError(null);
  }, [target]);

  const set = (patch: Partial<EditForm>) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!target) return;
    if (form.wsId == null) { setError(t('mobile.isr.err.workspace_required', 'A workspace is required.')); return; }
    if (!form.endpoint.trim()) { setError(t('mobile.isr.err.endpoint_required', 'An endpoint is required.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'isr_target', 'isr_target_id', target.isr_target_id, {
      name: form.name.trim() || target.internal_code,
      une_workspace_id: form.wsId,
      protocol: form.protocol,
      endpoint_url: form.endpoint.trim(),
      auth_mechanism: form.auth,
      secret_ref: form.secretRef.trim() || null,
      upp_definition_id: form.defId,
      schedule_kind: form.schedKind,
      cron_expr: form.schedKind === 'CRON' ? (form.cron.trim() || null) : null,
      interval_seconds: form.interval,
      timeout_ms: form.timeout,
      is_active: form.active ? 'Y' : 'N',
    });
    if (!e) {
      // (Re)register the cron job for CRON-scheduled targets — pg_cron is optional,
      // so this is best-effort and never blocks the save result.
      try { await apiDb.rpc('isr_set_schedule', { p_code: target.internal_code }); } catch { /* pg_cron optional */ }
    }
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = target !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.isr.edit', 'Edit target')}>
        <div className="m-grabber" />
        {target && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><DownloadCloud size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.isr.edit', 'Edit target')}</div>
                <div className="m-em">{target.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-isr-name">{t('mobile.isr.f.name', 'Name')}</label>
                <input id="m-isr-name" className="m-inp" value={form.name} onChange={(e) => set({ name: e.target.value })} autoFocus />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-isr-ws">{t('mobile.isr.f.workspace', 'Workspace')}</label>
                <select id="m-isr-ws" className="m-sel" value={form.wsId == null ? '' : String(form.wsId)} onChange={(e) => set({ wsId: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">{t('mobile.isr.select_workspace', 'Select a workspace…')}</option>
                  {ws.map((w) => <option key={w.une_workspace_id} value={String(w.une_workspace_id)}>{w.name}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-isr-protocol">{t('mobile.isr.f.protocol', 'Protocol')}</label>
                <select id="m-isr-protocol" className="m-sel" value={form.protocol} onChange={(e) => set({ protocol: e.target.value })}>
                  {PROTOCOL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.label)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-isr-endpoint">{t('mobile.isr.f.endpoint', 'Endpoint')}</label>
                <input id="m-isr-endpoint" className="m-inp" value={form.endpoint} onChange={(e) => set({ endpoint: e.target.value })} placeholder="https://api.acme.com/v1/orders" />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-isr-auth">{t('mobile.isr.f.auth', 'Auth')}</label>
                <select id="m-isr-auth" className="m-sel" value={form.auth} onChange={(e) => set({ auth: e.target.value })}>
                  {AUTH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.label)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-isr-secret">{t('mobile.isr.f.secret_ref', 'Secret ref (env name)')}</label>
                <input id="m-isr-secret" className="m-inp" value={form.secretRef} onChange={(e) => set({ secretRef: e.target.value })} placeholder="ACME_API_KEY" />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-isr-def">{t('mobile.isr.f.definition', 'Import definition')}</label>
                <select id="m-isr-def" className="m-sel" value={form.defId == null ? '' : String(form.defId)} onChange={(e) => set({ defId: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">{t('mobile.isr.no_definition', 'No import definition')}</option>
                  {defs.map((d) => <option key={d.upp_definition_id} value={String(d.upp_definition_id)}>{d.name || d.internal_code}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-isr-sched">{t('mobile.isr.f.schedule', 'Schedule')}</label>
                <select id="m-isr-sched" className="m-sel" value={form.schedKind} onChange={(e) => set({ schedKind: e.target.value })}>
                  {SCHEDULE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.label)}</option>)}
                </select>
              </div>
              {form.schedKind === 'CRON' ? (
                <div className="m-formrow">
                  <label htmlFor="m-isr-cron">{t('mobile.isr.f.cron', 'Cron expression')}</label>
                  <input id="m-isr-cron" className="m-inp" value={form.cron} onChange={(e) => set({ cron: e.target.value })} placeholder="*/15 * * * *" />
                </div>
              ) : (
                <div className="m-formrow">
                  <label htmlFor="m-isr-interval">{t('mobile.isr.f.interval', 'Interval (seconds)')}</label>
                  <input id="m-isr-interval" className="m-inp" type="number" value={form.interval} onChange={(e) => set({ interval: Number(e.target.value) })} />
                </div>
              )}
              <div className="m-formrow">
                <label htmlFor="m-isr-timeout">{t('mobile.isr.f.timeout', 'Timeout (ms)')}</label>
                <input id="m-isr-timeout" className="m-inp" type="number" value={form.timeout} onChange={(e) => set({ timeout: Number(e.target.value) })} />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.isr.active_hint', 'Target is polled on its schedule')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.active}
                  className={'m-switch ' + (form.active ? 'm-on' : 'm-off')}
                  onClick={() => set({ active: !form.active })}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
